import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ContentProps, PopupProps, TriggerProps } from '@atlaskit/popup/types';
import { Popup } from '@atlaskit/popup/popup';
import Spinner from '@atlaskit/spinner/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { FocusInsideOnLoad } from './focus-inside-on-load';
import { useClickToCloseRef } from './use-click-to-close-ref';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	onUnmount,
	shouldFocus,
}: {
	fallback?: ReactNode;
	onUnmount: () => void;
	shouldFocus: boolean;
}) => {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (shouldFocus) {
			ref.current?.focus();
		}

		return () => onUnmount();
	}, [onUnmount, shouldFocus]);

	return (
		<span ref={ref} tabIndex={shouldFocus ? -1 : undefined} data-testid="popup-trigger-loading">
			{fallback || <Spinner />}
		</span>
	);
};

export type PopupTriggerProps<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointProps<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	trigger: (triggerProps: TriggerProps) => ReactNode;
} & CustomPopupProps;

export function PopupTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	forcedReportErrorUFO,
	onClose,
	preloadOnFocus = true,
	autoFocus,
	...popupProps
}: PopupTriggerProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const updateRef = useRef<(() => void) | null>(null);
	const setInitialFocusRef = useRef<ContentProps['setInitialFocusRef'] | null>(null);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			setIsOpen(false);
			onClose?.(event, currentLevel);
		},
		[onClose],
	);

	const triggerCloseRef = useClickToCloseRef({
		isOpen,
		handleClose,
	});

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const popupSetInitialFocusRef = useRef<((el: HTMLElement | null) => void) | null>(null);
	const handleSetInitialFocusRef = useCallback((el: HTMLElement | null) => {
		popupSetInitialFocusRef.current?.(el);
	}, []);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			handleOpen();

			setContent(
				<>
					<InternalEntryPointContainer
						id={id}
						entryPointReference={entryPointReference}
						fallback={
							// when the fallback is unmounted we want to trigger an update to the popup position
							<FallbackComponent
								fallback={fallback}
								onUnmount={() => updateRef.current?.()}
								shouldFocus={fg('platform_dst-a11y_popup-focus-on-load') && autoFocus !== false}
							/>
						}
						errorFallback={errorFallback}
						runtimeProps={
							{
								...(entryPointProps ?? emptyEntryPointProps),
								onContentResized: () => {
									updateRef.current?.();
								},
								onClose: handleClose,
								...(fg('platform_dst-popup-trigger-initial-focus-ref')
									? { setInitialFocusRef: handleSetInitialFocusRef }
									: {}),
							} as unknown as EntryPointProps<TEntryPoint>
						}
						onError={onError}
						forcedError={forcedReportErrorUFO}
					/>
					{fg('platform_dst-a11y_popup-focus-on-load') &&
						// Omit means `Popup`'s own default (`true`). Only an explicit
						// `false` opts out of post-load focus.
						autoFocus !== false && (
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								setInitialFocusRef={setInitialFocusRef}
							/>
						)}
				</>,
			);
		},
		[
			setContent,
			id,
			isOpen,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
			handleSetInitialFocusRef,
			autoFocus,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, triggerRef, triggerCloseRef],
	);

	const popupContent = useCallback(
		({ update, setInitialFocusRef }: ContentProps) => {
			updateRef.current = update;
			if (fg('platform_dst-popup-trigger-initial-focus-ref')) {
				popupSetInitialFocusRef.current = setInitialFocusRef;
			}
			return content;
		},
		[content],
	);

	return (
		<Popup
			// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
			{...popupProps}
			autoFocus={autoFocus}
			isOpen={isOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
		/>
	);
}
